// Near-expiry and earliest-expiry-first rules shared by every screen that
// builds a pharmacy cart: DispensingTab.tsx, the Visit Workflow's
// PharmacyStage.tsx and the walk-in counter's OutpatientTab.tsx. All three
// sell through the same backend engine (utils/pharmacySale.js), which
// enforces the later-batch rule itself via utils/pharmacyExpiry.js — this
// file mirrors that rule so the pharmacist resolves it before checkout rather
// than meeting it as a 400.
//
//   Near Expiry   fewer than `nearExpiryDays` days left (the hospital's
//                 window, 90 by default). A warning only — never blocks.
//
//   Later batch   a cart line on batch L while an earlier-expiring,
//                 non-expired batch E of the same medicine has enough stock
//                 free for the whole line. "Free" subtracts whatever the rest
//                 of the cart already takes from E, exactly as the backend
//                 does, so splitting a quantity across batches in expiry
//                 order never asks for a reason.
//
// Every function takes the minimal shape it needs rather than any screen's
// own CartItem/InventoryItem type — same convention as dispensingCart.ts.

export interface ExpiryBatch {
  id: number;
  medicine_id: number;
  batch_number: string;
  expiry_date: string;
  quantity_available: number;
}

export interface BatchSelectionLine {
  inventory_id: number;
  medicine_id: number;
  qty: number;
  expiry: string;
  batch_selection_reason?: string;
  batch_selection_remarks?: string;
}

const DAY_MS = 86_400_000;

const localMidnight = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

/** Whole days from today to the expiry date (0 = expires today, negative = past). */
export function daysToExpiry(expiry: string | null | undefined, today: Date = new Date()): number | null {
  if (!expiry) return null;
  const d = new Date(expiry);
  if (Number.isNaN(d.getTime())) return null;
  return Math.round((localMidnight(d) - localMidnight(today)) / DAY_MS);
}

/**
 * Inside the window but not yet expired. Same boundary as the backend's
 * Near-Expiry report and the stock report's "Expiring soon":
 * expiry_date < today + near_expiry_days.
 */
export function isNearExpiry(expiry: string | null | undefined, nearExpiryDays: number): boolean {
  const days = daysToExpiry(expiry);
  return days != null && days >= 0 && days < nearExpiryDays;
}

const byExpiry = (a: ExpiryBatch, b: ExpiryBatch) =>
  new Date(a.expiry_date).getTime() - new Date(b.expiry_date).getTime() || a.id - b.id;

/**
 * The batch to auto-select for `qty` units of one medicine: the earliest-
 * expiring batch that can cover the whole quantity, else (nothing can) the
 * earliest with any stock left. `batches` must already be the dispensable set
 * (in stock, not expired) for that one medicine; `cart`, when given, is the
 * cart the new line joins, so units it already takes are not counted twice.
 */
export function pickFefoBatch<T extends ExpiryBatch>(
  batches: T[],
  qty = 1,
  cart: Pick<BatchSelectionLine, 'inventory_id' | 'qty'>[] = []
): T | null {
  const free = (b: T) => b.quantity_available - cart.reduce((s, l) => (l.inventory_id === b.id ? s + l.qty : s), 0);
  const sorted = [...batches].sort(byExpiry);
  return sorted.find((b) => free(b) >= qty) ?? sorted.find((b) => free(b) > 0) ?? sorted[0] ?? null;
}

/**
 * The earlier batch that makes `line` a later-batch selection, or null when
 * the line already follows earliest-expiry-first. `dispensable` is every
 * in-stock, non-expired batch at this branch; `cart` is the whole cart
 * (including `line`) so units other lines claim are not counted as free.
 */
export function earlierBatchFor<T extends ExpiryBatch>(
  line: BatchSelectionLine,
  dispensable: T[],
  cart: Pick<BatchSelectionLine, 'inventory_id' | 'qty'>[]
): T | null {
  const lineExpiry = new Date(line.expiry).getTime();
  const claimed = (id: number) => cart.reduce((s, l) => (l.inventory_id === id ? s + l.qty : s), 0);
  return (
    dispensable
      .filter((b) => b.medicine_id === line.medicine_id && b.id !== line.inventory_id && new Date(b.expiry_date).getTime() < lineExpiry)
      .sort(byExpiry)
      .find((b) => b.quantity_available - claimed(b.id) >= line.qty) ?? null
  );
}

export interface LaterBatchIssue<L> {
  line: L;
  earlier: ExpiryBatch;
}

/** Cart lines that sell a later batch and do not carry a reason yet. */
export function unresolvedLaterBatches<L extends BatchSelectionLine>(items: L[], dispensable: ExpiryBatch[]): LaterBatchIssue<L>[] {
  const out: LaterBatchIssue<L>[] = [];
  for (const line of items) {
    if (line.batch_selection_reason) continue;
    const earlier = earlierBatchFor(line, dispensable, items);
    if (earlier) out.push({ line, earlier });
  }
  return out;
}

/**
 * Applies one reason (+ optional remarks) to every cart line named in
 * `targetInventoryIds` — the "Apply" action on the later-batch banner, same
 * one-shared-field pattern as dispensingCart.ts's applyReasonToLines.
 */
export function applyBatchSelectionReason<L extends BatchSelectionLine>(
  items: L[],
  targetInventoryIds: Set<number>,
  reason: string,
  remarks: string
): L[] {
  const trimmed = remarks.trim();
  return items.map((it) =>
    targetInventoryIds.has(it.inventory_id)
      ? { ...it, batch_selection_reason: reason, batch_selection_remarks: trimmed || undefined }
      : it
  );
}
